import { useMemo } from 'react';
import { isEligibleForConditionalPropertyOptions } from '../conditionalPropertyOptionsRules/utils';
import { getActivePropertyDefinitionWithFilteredOptions } from '../utils/getActivePropertyDefinitionWithFilteredOptions';
import { useAllPropertyValues } from './useAllPropertyValues';
import { usePropertyDefinitions } from './usePropertyDefinitions';
import { useConditionalPropertyOptionsRulesForObjectType } from './useConditionalPropertyOptionsRulesForObjectType';
import { getOwnProperty } from '../../utils/getOwnProperty';
export const useConditionalPropertyOptions = ({
  propertyDefinition,
  enableConditionalPropertyOptions = false,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue,
  value
}) => {
  const shouldCheckForCPO = isEligibleForConditionalPropertyOptions({
    enableConditionalPropertyOptions,
    propertyDefinition: propertyDefinition,
    objectTypeId
  });
  const {
    data: conditionalPropertyOptionsRules,
    loading: conditionalPropertyOptionsRulesLoading,
    error: conditionalPropertyOptionsRulesError
  } = useConditionalPropertyOptionsRulesForObjectType(objectTypeId, {
    skip: !shouldCheckForCPO
  });
  const {
    data: propertyDefinitions,
    loading: propertyDefinitionsLoading,
    error: propertyDefinitionsError
  } = usePropertyDefinitions(objectTypeId, {
    skip: !shouldCheckForCPO
  });
  const {
    data: allPropertyValues,
    loading: allPropertyValuesLoading,
    error: allPropertyValuesError
  } = useAllPropertyValues({
    objectId,
    objectTypeId,
    skip: !shouldCheckForCPO
  });
  const conditionalPropertyOptionsRulesForProperty = useMemo(() => {
    return (conditionalPropertyOptionsRules || []).filter(rule => rule.dependentFieldName === propertyDefinition.name);
  }, [conditionalPropertyOptionsRules, propertyDefinition.name]);
  return useMemo(() => {
    if (conditionalPropertyOptionsRulesLoading || propertyDefinitionsLoading || allPropertyValuesLoading) {
      return {
        loading: true,
        error: undefined,
        data: undefined
      };
    }
    const error = conditionalPropertyOptionsRulesError || propertyDefinitionsError || allPropertyValuesError;
    if (error) {
      return {
        loading: false,
        error,
        data: undefined
      };
    }
    if (!shouldCheckForCPO || conditionalPropertyOptionsRulesForProperty.length === 0 || propertyDefinitions === undefined) {
      return {
        loading: false,
        error: undefined,
        data: undefined
      };
    }

    // CPO limits to one controlling field per property, so we can safely assume the first
    // field will match all other fields
    const controllingFieldName = conditionalPropertyOptionsRulesForProperty[0].controllingField.fieldName;
    const controllingFieldPropertyDefinition = getOwnProperty(propertyDefinitions, controllingFieldName);
    const additionalPropertyValue = getAdditionalPropertyValue(controllingFieldName, objectId, objectTypeId);
    const controllingFieldCurrentValue = additionalPropertyValue !== undefined ? additionalPropertyValue : allPropertyValues ? getOwnProperty(allPropertyValues, controllingFieldName) : undefined;
    return {
      loading: false,
      error: undefined,
      data: getActivePropertyDefinitionWithFilteredOptions({
        conditionalPropertyOptionsRulesForProperty,
        controllingFieldCurrentValue,
        controllingFieldPropertyDefinition,
        originalPropertyDefinition: propertyDefinition,
        value
      })
    };
  }, [allPropertyValues, allPropertyValuesError, allPropertyValuesLoading, conditionalPropertyOptionsRulesError, conditionalPropertyOptionsRulesForProperty, conditionalPropertyOptionsRulesLoading, getAdditionalPropertyValue, objectId, objectTypeId, propertyDefinition, propertyDefinitions, propertyDefinitionsError, propertyDefinitionsLoading, shouldCheckForCPO, value]);
};